import { Dispatch, SetStateAction, useCallback, useRef } from "react";
import { applyRangeSelection } from "./rangeSelection";
import { ItemKey } from "./types";

type Args<K extends ItemKey> = {
  setSelectedKeys: Dispatch<SetStateAction<Set<K>>>;
  /**
   * Rendered order, read at click time — a table sorts and pages independently of
   * the component holding the selection, so this can't be captured at render.
   */
  getOrderedKeys: () => K[];
};

/** Shift-click ranges over a Set of keys, for the results table. */
export function useRowRangeSelection<K extends ItemKey>({
  setSelectedKeys,
  getOrderedKeys,
}: Args<K>) {
  const anchorRef = useRef<K | null>(null);

  const toggle = useCallback(
    (key: K, selected: boolean, shiftKey: boolean) => {
      // Read and advance the anchor here, never inside the updater below: React
      // defers that, by which point the ref holds the key just clicked.
      const anchorKey = anchorRef.current;
      anchorRef.current = key;

      const orderedKeys = getOrderedKeys();

      setSelectedKeys((previous) => {
        const { selectedKeys } = applyRangeSelection({
          orderedKeys,
          selectedKeys: [...previous],
          anchorKey,
          clickedKey: key,
          shiftKey,
          selected,
        });
        return new Set(selectedKeys as K[]);
      });
    },
    [getOrderedKeys, setSelectedKeys],
  );

  /** Drop the anchor when the rows it referred to are gone (search, paging, clear). */
  const clearAnchor = useCallback(() => {
    anchorRef.current = null;
  }, []);

  return { toggle, clearAnchor };
}
